import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectService } from '../services/project.service';
import type { ProjectFilters, CreateProjectRequest, UpdateProjectRequest } from '../types';

export function useProjects(params?: ProjectFilters) {
  return useQuery({
    queryKey: ['projects', params],
    queryFn: () => projectService.getAll(params),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => projectService.getById(id),
    enabled: !!id,
  });
}

export function useProjectsByClient(clientId: string) {
  return useQuery({
    queryKey: ['projects', 'client', clientId],
    queryFn: () => projectService.getByClient(clientId),
    enabled: !!clientId,
  });
}

export function useProjectCSATTimeline(id: string) {
  return useQuery({
    queryKey: ['projects', id, 'csat-timeline'],
    queryFn: () => projectService.getCSATTimeline(id),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProjectRequest) => projectService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProjectRequest) => projectService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', id] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => projectService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
